import { Space, type TableColumnsType } from "antd";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useParams } from "react-router-dom";
import PermissionCheck from "~Common/PermissionCheck";
import type { AgeGroups } from "~enums/ageGroups";
import type { Genders } from "~enums/genders";
import { PermissionNames } from "~enums/permissions";
import type { SeasonStatuses } from "~enums/seasonStatuses";
import usePermissionsByCompetitionName from "~hooks/usePermissionsByCompetitionName";
import type { SeasonTransformed } from "../types";

const useGetSeasonsTableColumns = (): TableColumnsType<SeasonTransformed> => {
  const { formatMessage } = useIntl();
  const { sport, customerId, organizationId } = useParams();
  const { seasonEntityPermissionName } = usePermissionsByCompetitionName();

  return [
    {
      key: "year",
      title: formatMessage({ id: "year" }),
      dataIndex: "year",
      width: 60,
      sorter: (a, b) => (a.year || 0) - (b.year || 0),
    },
    {
      key: "competitionName",
      title: formatMessage({ id: "competitionname" }),
      dataIndex: "competitionName",
      width: 300,
      ellipsis: { showTitle: true },
      sorter: (a, b) => a.competitionName?.localeCompare(b.competitionName),
    },
    {
      key: "nameLocal",
      title: formatMessage({ id: "season.title" }),
      dataIndex: "nameLocal",
      width: 300,
      ellipsis: { showTitle: true },
      sorter: (a, b) => a.nameLocal?.localeCompare(b.nameLocal),
    },
    {
      key: "gender",
      title: formatMessage({ id: "gender" }),
      dataIndex: "gender",
      width: 100,
      sorter: (a, b) => a.gender?.localeCompare(b.gender),
      render: (value: Genders) => formatMessage({ id: `gender.${value}` }),
    },
    {
      key: "ageGroup",
      title: formatMessage({ id: "ageGroup" }),
      dataIndex: "ageGroup",
      width: 100,
      sorter: (a, b) => a.ageGroup?.localeCompare(b.ageGroup),
      render: (value: AgeGroups) => formatMessage({ id: `ageGroup.${value}` }),
    },
    {
      key: "status",
      title: formatMessage({ id: "status" }),
      dataIndex: "status",
      width: 100,
      sorter: (a, b) => a.status?.localeCompare(b.status),
      render: (value: SeasonStatuses) => formatMessage({ id: `seasonStatus.${value}` }),
    },
    {
      align: "center",
      fixed: "right",
      width: 270,
      render: (_, { seasonId }) => (
        <Space size={16} onClick={(e) => e.stopPropagation()}>
          <PermissionCheck permissionKeys={[PermissionNames.FIXTURE]}>
            <Link to={`/${customerId}/admin/${sport}/${organizationId}/seasons/${seasonId}/fixtures`}>
              <FormattedMessage id="fixtures" />
            </Link>
          </PermissionCheck>
          <PermissionCheck permissionKeys={[PermissionNames.SEASON]}>
            <Link to={`/${customerId}/admin/${sport}/${organizationId}/seasons/${seasonId}/details`}>
              <FormattedMessage id="details" />
            </Link>
          </PermissionCheck>
          <PermissionCheck permissionKeys={[seasonEntityPermissionName as PermissionNames]}>
            <Link to={`/${customerId}/admin/${sport}/${organizationId}/seasons/${seasonId}/entities`}>
              <FormattedMessage id="entities" />
            </Link>
          </PermissionCheck>
          <PermissionCheck permissionKeys={[PermissionNames.STANDING]}>
            <Link to={`/${customerId}/admin/${sport}/${organizationId}/seasons/${seasonId}/standings`}>
              <FormattedMessage id="standings" />
            </Link>
          </PermissionCheck>
        </Space>
      ),
    },
  ];
};

export default useGetSeasonsTableColumns;
